import BlockContent from '@/components/BlockContent';
import CtaButton from '@/components/ui/buttons/CtaButton';
import React from 'react';

type SingleColumnEntry = {
  _key: string;
  title: string;
  content?: Record<string, unknown>[];
  alignment?: 'left' | 'center' | 'right';
  bgColor?: { hex: string };
};

interface ColumnsLayoutProps {
  title?: string;
  heading?: string;
  columnsSize?: 1 | 2 | 3 | 4;
  items: SingleColumnEntry[];
  cta?: {
    title: string;
    route?: string;
    link?: string;
  };
  bgColor?: { hex: string };
  bgGradient?: {
    gradientDirection: string;
    colorStart: { hex: string };
    colorStop: { hex: string };
  };
}

const ColumnsLayout: React.FC<ColumnsLayoutProps> = ({
  title,
  heading,
  columnsSize = 3,
  items,
  cta,
  bgColor,
  bgGradient,
}) => {
  const background =
    (bgColor && bgColor.hex) ||
    (bgGradient?.colorStart?.hex &&
      bgGradient?.colorStop?.hex &&
      `-webkit-linear-gradient(${bgGradient.gradientDirection}, ${bgGradient.colorStart.hex} 0%, ${bgGradient.colorStop.hex} 100%)`);
  const columnsClass =
    columnsSize === 1
      ? 'sm:grid-cols-1'
      : columnsSize === 2
        ? 'sm:grid-cols-2'
        : columnsSize === 4
          ? 'sm:grid-cols-4'
          : 'sm:grid-cols-3';

  return (
    <section className="w-full px-5 py-[60px]" style={background ? { background } : undefined}>
      <div className="mx-auto w-full max-w-[1200px]">
        {title && <p className="mb-2 text-sm uppercase tracking-[0.1em] opacity-70">{title}</p>}
        {heading && <h2 className="mb-8 text-[2rem]">{heading}</h2>}
        <div className={`grid grid-cols-1 gap-6 ${columnsClass}`}>
          {(items || []).map((col, index) => (
            <div
              key={col._key || index}
              className="rounded-lg p-6"
              style={{
                textAlign: col.alignment || 'left',
                backgroundColor: col.bgColor?.hex || undefined,
              }}
            >
              <h3 className="mb-3 text-xl font-bold">{col.title}</h3>
              {col.content && (
                <div className="text-base">
                  <BlockContent content={col.content} />
                </div>
              )}
            </div>
          ))}
        </div>
        {cta && (cta.route || cta.link) && (
          <div className="mt-8 text-center">
            <CtaButton to={cta.route || cta.link}>{cta.title}</CtaButton>
          </div>
        )}
      </div>
    </section>
  );
};

export default ColumnsLayout;
